import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { LocationDto, DropDownLocationsStore } from '../../../stores/configuration/locations/locations-store';
import { nameof } from '../../../utils/object';
import { QueryResult, OrderDefinition } from 'stores/dataStore';
import TailwindMultiSelect from '../../editors/tailwind-multiselect';

interface LocationEditorProps {
  value: LocationDto[];
  onChange?: (value: LocationDto[]) => void;
  locations?: DropDownLocationsStore;
  placeholder?: string;
  clearable?: boolean;
  className?: string;
  locationsReceived?: string[];
  showCountry?: boolean;
  disabled?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindMultipleLocationEditor: React.FC<LocationEditorProps> = ({
  value,
  onChange,
  locations,
  placeholder,
  clearable = true,
  className = '',
  locationsReceived,
  showCountry = true,
  disabled = false,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<LocationDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const mapToOptions = useCallback(
    (locations: QueryResult<LocationDto>) => {
      return (locations?.items || []).map(location => ({
        ...location,
        location:
          !showCountry || !location || (location.code !== 'XXXX' && location.code !== 'XXXXX')
            ? `${location.location} - ${location.countryName}`
            : location.location
      }));
    },
    [showCountry]
  );

  const getOptionsMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      let filter;
      const filterActive = `active eq true`;
      const orderBy: OrderDefinition[] = [{ direction: 'Ascending', field: nameof<LocationDto>('location'), useProfile: false }];

      if (locationsReceived && locationsReceived.length > 0) {
        filter = [{ id: { in: { type: 'guid', value: locationsReceived } } }, filterActive];
      } else {
        filter = [filterActive];
      }

      const result = await locations?.getAllAsync({ searchQuery: '', skip: 0, take: 100000, orderBy, filter }, locationsReceived);

      if (result) {
        setOptions(mapToOptions(result));
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [locationsReceived, locations, mapToOptions]);

  const handleChange = (newValue: LocationDto[]) => {
    if (onChange) {
      const selectedLocations = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
      onChange(selectedLocations);
    }
  };

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, options.length, getOptionsMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, getOptionsMethod]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(location => ({
              id: location.id,
              text: location.location
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(v => ({
        id: v.id,
        text: v.location
      }))}
      onChange={handleChange}
      placeholder={placeholder || t('Select location')}
      clearable={clearable}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      disabled={disabled || isLoading}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['locations', DropDownLocationsStore])(TailwindMultipleLocationEditor);
